import { useState } from "react";

import clsx from "clsx";
import { twMerge } from "../../types/twMerge";
import { IDescription, IOption } from "../../types/customizer";
import If from "../If";
import MaterialInfo from '../SwatchCategory/MaterialInfo';
import './index.scss';
import AppModal from "../AppModal";
import { DEFAULT_STYLES } from "../../../utils/basic";
import { APP_STYLES } from "../../../utils/basic";
import { useRingCustomizerContext } from "../../context/RingCustomizerContext";
import { sendAnalytics } from '../../../utils/analytics';

interface ISwatchOption extends IOption {
  hasSwatch: boolean;
  fieldType: string;
  onChange: (updated: any) => void;
  selectedOption: IOption;
  isChanging: string;
  moreInfo?: IDescription | null;
  hideMoreInfo?: boolean;
  evenGrid?: boolean;
  tab?: string;
  onSelectedClick?: () => void;
}

const SwatchOption = ({ type, name, short_name, swatch_url, selected, sku, additionalClassName, hasSwatch, fieldType, tab, onChange,
  selectedOption, isChanging, moreInfo, hideMoreInfo, evenGrid, onSelectedClick }: ISwatchOption) => {

  const [open, setOpen] = useState(false)
  const { subpath } = useRingCustomizerContext();

  const onClose = () => {
    setOpen(false)
  }

  const openModal = () => {
    if (moreInfo) {
      sendAnalytics(
        'more info',
        `More info click - ${tab} - ${name}`,
        subpath
      );
    }

    setOpen(true)
  }

  const handleClick = () => {
    if (!selected) {
      onChange({ [fieldType]: sku });
    } else if (onSelectedClick) {
      onSelectedClick();
    } else {
      if(selectedOption.has_inside_reversed_option || selectedOption.has_outside_reversed_option) {
        if(selectedOption.is_reverse_selected) {
          onChange({[fieldType]: sku})
        } else {
          onChange({[fieldType]: sku + 'REV'})
        }
      }
    }
  };

  const getUrl = (swatch_url: string) => {
    if (["legacy-touch", "legacy-touch-dev"].includes(subpath) && swatch_url === "/swatches/patterns/MFEATUREGROUP/images/1") {
      return 'https://assets.lashbrookdesigns.com/rings/profiles/D/widths/8/ring_materials/T/images/1?ring_finish=POLISH&finish_type=F&size=10&feature_group=M%2CN&machine_pattern=LCVFINGERPRINT2_T_COLOR_NONE&image_size=180&watermark=false';
    } else if (["legacy-touch", "legacy-touch-dev"].includes(subpath) && swatch_url === "/swatches/insidefeatures/D/images/1") {
      return 'https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/training/fingerprint-engraving%5B79%5D.jpg.png';
    } else {
      return swatch_url?.indexOf('http') !== -1 ? swatch_url : `${process.env.REACT_APP_ASSET_URL}${swatch_url}?image_size=180x180`;
    }
  }

  const styles = APP_STYLES[subpath] || DEFAULT_STYLES;
  if(hasSwatch) {
    return (
      <div className={clsx({ "sm:tw-w-32 tw-text-center sm:tw-mr-3 tw-my-2 tw-relative tw-flex-shrink-0": true, "tw-w-24 tw-mr-1": !evenGrid, "tw-w-1/4": evenGrid })}>
        <div className="xs:tw-px-1.5 sm:tw-px-2">
          <div className={clsx("tw-inline-block tw-relative",
          {
            "tw-border tw-border-brand-color": selected && !isChanging,
            "tw-border-dashed": styles.isDashedBorder && selected && !isChanging ,
            "tw-border-solid": !styles.isDashedBorder && selected && !isChanging ,
            "tw-rounded-full": styles.isRoundedBorder && selected && !isChanging ,
            "rounded-square-selected": !styles.isRoundedBorder && selected && !isChanging,
            "selected-border": isChanging.includes(sku) && styles.isRoundedBorder,
            "selected-border-square": isChanging.includes(sku) && !styles.isRoundedBorder,

          })}
            onClick={handleClick}>
            <div className={clsx("tw-block tw-relative xs:tw-w-16 xs:tw-h-16 sm:tw-w-22.5 sm:tw-h-22.5 tw-cursor-pointer tw-bg-no-repeat tw-bg-center tw-bg-contain tw-m-1",
              {
                'tw-rounded-full' : styles.isRoundedBorder,
                "rounded-square" : !styles.isRoundedBorder,
              })}
              style={{ backgroundImage: `url(${getUrl(swatch_url)})` }} >
                <If condition={styles.enableMoreInfo}>
                  {(!isChanging && selected && moreInfo && !hideMoreInfo) &&
                    <div onClick={openModal} className={clsx("moreInfo-overlay tw-bg-brand-color xs:tw-text-xs sm:tw-text-sm xs:tw-pt-0.5 sm:tw-pt-1",
                      {
                        "!tw-rounded-b-none": !styles.isRoundedBorder,
                      })}>
                      more info
                    </div>
                  }
                </If>
            </div>
          </div>
          {/* Show only for md devices (768px) */}
          <div className="secondary-font-family tw-text-sm tw-text-secondary-color tw-mt-1 tw-pl-1 md:tw-block tw-hidden">{name}</div>
          {/* Show only for less than md devices (768px) */}
          <div className="secondary-font-family tw-text-sm tw-text-secondary-color tw-mt-1 tw-pl-1 md:tw-hidden tw-block">{short_name || name}</div>
        </div>
        <AppModal size="xl" isOpen={open} onClose={onClose} adjustToContent>
          {moreInfo && <MaterialInfo {...moreInfo} tab={tab} />}
        </AppModal>
      </div>)
  } else {
    return (
      <div className={clsx({ "tw-p-1.5 sm:tw-p-2 tw-cursor-pointer tw-relative": true, "selected-border-square ": isChanging === sku })}>
        <If condition={name || sku}>
          <div className={twMerge(clsx({
            "tw-p-1.5 sm:tw-p-2 tw-border tw-border-solid tw-rounded tw-text-center min-w-18 secondary-font-family tw-text-sm tw-text-secondary-color": true,
            "tw-border-secondary-border-color": !selected || isChanging,
            "tw-border-dashed tw-border-brand-color": selected && !isChanging
          }), `${additionalClassName}`)} onClick={handleClick}>
            {sku === 'CUSTOM' ? sku : name}
          </div>
        </If>
      </div>
    )
  }
}

export default SwatchOption;